import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Activity, ChevronDown, MessageCircle, Loader2, Pause, XCircle, CheckCircle2, Clock, MoreVertical, Play, Trash2, StopCircle } from 'lucide-react';
import { formatPauseReason, getCurrentStepTitle } from '../../../firebase/automationRuns.service';

/**
 * Format scheduled resume time for display
 * @param {Date|Object} timestamp - Firestore timestamp or Date
 * @returns {string} Human-readable relative time
 */
function formatResumeTime(timestamp) {
  if (!timestamp) return '';

  // Convert Firestore timestamp to Date if needed
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'soon';
  }

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }
  if (diffHours > 0) {
    return `in ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  }
  if (diffMins > 0) {
    return `in ${diffMins} min${diffMins > 1 ? 's' : ''}`;
  }
  return 'soon';
}

/**
 * Maps execution status from Firestore to display format
 */
function getDisplayStatus(status) {
  if (!status) return 'Unknown';
  const statusMap = {
    running: 'Running',
    waiting: 'Waiting',    // NEW: Wait step in progress
    paused: 'Paused',
    completed: 'Completed',
    failed: 'Failed',
    rejected: 'Stopped',
    pending: 'Pending',
  };
  return statusMap[status.toLowerCase()] || status;
}

/**
 * Gets status badge styling
 */
function getStatusStyle(status) {
  const displayStatus = getDisplayStatus(status);
  switch (displayStatus) {
    case 'Running':
      return 'bg-orange-50 text-orange-600 border-orange-100';
    case 'Waiting':
      return 'bg-blue-50 text-blue-600 border-blue-100';  // NEW: Blue for waiting
    case 'Paused':
      return 'bg-amber-50 text-amber-600 border-amber-100';
    case 'Completed':
      return 'bg-green-50 text-green-600 border-green-100';
    case 'Failed':
      return 'bg-red-50 text-red-600 border-red-100';
    case 'Stopped':
    default:
      return 'bg-stone-100 text-stone-600 border-stone-200';
  }
}

/**
 * Gets status icon component
 */
function StatusIcon({ status }) {
  const displayStatus = getDisplayStatus(status);
  switch (displayStatus) {
    case 'Running':
      return <Loader2 className="w-3 h-3 animate-spin" />;
    case 'Waiting':
      return <Clock className="w-3 h-3" />;  // NEW: Clock icon for waiting
    case 'Paused':
      return <Pause className="w-3 h-3" />;
    case 'Completed':
      return <CheckCircle2 className="w-3 h-3" />;
    case 'Failed':
    case 'Stopped':
      return <XCircle className="w-3 h-3" />;
    default:
      return null;
  }
}

const LeadsMonitorPanel = ({
  showLeadsPanel,
  runs = [],           // Real Firestore runs (new format)
  monitorLeads = [],   // Legacy mock data (deprecated)
  leads = [],          // Live leads from Firestore (for resolving when snapshots missing)
  leadsLoading = false, // Whether leads are still loading
  onClose,
  onPause,             // Callback: (runId) => void
  onResume,            // Callback: (runId) => void
  onStop,              // Callback: (runId) => void
  onDelete,            // Callback: (runId) => void
  isLoading = false,
  runStats = {},       // { total, running, paused, completed, failed }
  // ========== FILTER PROPS (Firestore query-level filtering) ==========
  statusFilter = 'all',        // Current status filter value
  sourceFilter = 'all',        // Current source filter value
  onStatusFilterChange,        // (value) => void - Change status filter
  onSourceFilterChange,        // (value) => void - Change source filter
  isMobile
}) => {
  // Build a lookup map of leads by ID for O(1) resolution
  const leadsMap = useMemo(() => {
    const map = {};
    leads.forEach(lead => {
      if (lead?.id) map[lead.id] = lead;
    });
    return map;
  }, [leads]);

  // ========== DEBUG: Phase 1-4 — trace resolution pipeline ==========
  useEffect(() => {
    console.group('[LeadMonitor DEBUG]');

    // Phase 2 — leads state
    console.log('leadsLoading:', leadsLoading);
    console.log('leads.length:', leads.length);
    console.log('leads (first 3):', leads.slice(0, 3).map(l => ({ id: l?.id, name: l?.name || l?.fullName })));
    console.log('leadsMap keys:', Object.keys(leadsMap));

    // Phase 1 — raw execution data
    console.log('runs.length:', runs.length);
    runs.forEach((run, i) => {
      const resolvedLead = run.leadId ? leadsMap[run.leadId] : null;
      console.log(`--- RUN[${i}] ---`, {
        // Phase 1
        runId: run.id,
        leadId: run.leadId,
        leadIdType: typeof run.leadId,
        leadSnapshot: run.leadSnapshot,
        propertySnapshot: run.propertySnapshot,
        formData: run.formData,
        // Phase 3 — matching
        resolvedLead: resolvedLead ? { id: resolvedLead.id, name: resolvedLead.fullName || resolvedLead.name } : null,
        matchFound: !!resolvedLead,
        // Phase 4 — property data from resolved lead
        liveInterestedProperties: resolvedLead?.interestedProperties,
      });
    });

    console.groupEnd();
  }, [runs, leads, leadsMap, leadsLoading]);
  // ========== END DEBUG ==========

  // Normalize data: support both legacy mock format and new Firestore format
  const normalizedRuns = useMemo(() => {
    // If we have real runs from Firestore, use those
    if (runs.length > 0) {
      return runs.map(run => {
        // ===== LEAD RESOLUTION =====
        // Priority: 1) execution snapshot  2) live leads lookup  3) formData  4) fallback
        const liveLead = run.leadId ? leadsMap[run.leadId] : null;
        const snapshot = run.leadSnapshot;

        const leadName = snapshot?.fullName
          || snapshot?.firstName
          || liveLead?.fullName
          || liveLead?.name
          || liveLead?.firstName
          || run.formData?.firstName
          || run.formData?.name
          || (leadsLoading && run.leadId ? 'Loading…' : 'Unknown Lead');

        const leadEmail = snapshot?.email
          || liveLead?.email
          || run.formData?.email
          || '';

        // ===== PROPERTY RESOLUTION =====
        // Priority: 1) execution propertySnapshot  2) live lead's interestedProperties  3) formData  4) fallback
        let properties = run.propertySnapshot || [];

        // If snapshot is empty, resolve from live lead's interestedProperties
        if (properties.length === 0 && liveLead) {
          const ip = liveLead.interestedProperties;
          if (Array.isArray(ip) && ip.length > 0) {
            properties = ip.map(p => ({
              id: p.id || '',
              name: p.name || p.address || 'Unknown Property',
              address: p.address || '',
            }));
          }
        }

        // Format property display: "Name" for 1, "Name +N" for multiple
        let propertyDisplay = 'Unknown';
        if (properties.length === 1) {
          propertyDisplay = properties[0].name || properties[0].address || 'Unknown Property';
        } else if (properties.length > 1) {
          const firstName = properties[0].name || properties[0].address || 'Unknown Property';
          propertyDisplay = `${firstName} +${properties.length - 1}`;
        } else {
          // Fallback sources when both snapshot and live lead are empty
          propertyDisplay = run.formData?.propertyName
            || run.formData?.property
            || run.formData?.propertyAddress
            || (leadsLoading && run.leadId ? 'Loading…' : 'No Property');
        }

        // Compute step display info
        const totalSteps = run.automationSnapshot?.steps?.length || 0;
        const currentStepIndex = run.currentStepIndex ?? 0;

        // For completed runs, show final step; for others, ensure index is valid
        const displayStepIndex = run.status === 'completed'
          ? Math.max(0, totalSteps - 1)
          : Math.min(currentStepIndex, totalSteps - 1);

        return {
          id: run.id,
          // Lead info — resolved from snapshot or live data
          name: leadName,
          email: leadEmail,
          // Property info - formatted as "Name +N"
          property: propertyDisplay,
          extra: null, // No longer using separate extra field
          // Step info - use displayStepIndex for valid display
          step: getCurrentStepTitle(run),
          currentStepIndex: displayStepIndex,
          totalSteps: totalSteps,
          isCompleted: run.status === 'completed',
          // Status
          status: run.status,
          displayStatus: getDisplayStatus(run.status),
          // Reason/notes - for waiting status, show resume time
          note: run.status === 'waiting' && run.scheduledResumeAt
            ? `Resumes ${formatResumeTime(run.scheduledResumeAt)}`
            : (formatPauseReason(run.pauseReason, run.pauseDetails) || '-'),
          pauseReason: run.pauseReason,
          pauseDetails: run.pauseDetails,
          // Wait-specific data
          scheduledResumeAt: run.scheduledResumeAt,
          waitingDetails: run.waitingDetails,
          // Timestamps
          startedAt: run.startedAt,
          updatedAt: run.updatedAt,
          // Source (derived from trigger.type)
          source: run.trigger?.type === 'FORM_SUBMIT' ? 'Form' : 'Manual',
          // Raw data for actions
          _raw: run,
        };
      });
    }

    // Fallback to legacy mock data format
    return monitorLeads.map((lead, index) => ({
      id: `mock-${index}`,
      name: lead.name,
      email: lead.email || '',
      property: lead.property,
      extra: lead.extra,
      step: lead.step,
      status: lead.status?.toLowerCase() || 'unknown',
      displayStatus: lead.status,
      note: lead.note,
      source: 'Mock',
      _isMock: true,
    }));
  }, [runs, monitorLeads, leadsMap, leadsLoading]);

  // Compute display stats
  const displayStats = useMemo(() => {
    if (runStats.total > 0) {
      // Combine running + waiting for "in progress" count
      const inProgress = (runStats.running || 0) + (runStats.waiting || 0);
      return {
        entered: runStats.total,
        completed: runStats.completed,
        running: inProgress,  // Shows all "in progress" leads (running + waiting)
        waiting: runStats.waiting || 0,
        paused: runStats.paused,
      };
    }
    // Fallback: compute from normalized runs
    const stats = { entered: 0, completed: 0, running: 0, waiting: 0, paused: 0 };
    normalizedRuns.forEach(run => {
      stats.entered++;
      const status = (run.status || '').toLowerCase();
      if (status === 'completed') stats.completed++;
      else if (status === 'running') stats.running++;
      else if (status === 'waiting') {
        stats.waiting++;
        stats.running++; // Also count as "in progress"
      }
      else if (status === 'paused') stats.paused++;
    });
    return stats;
  }, [runStats, normalizedRuns]);

  // Progress bar percentages
  const progressPcts = useMemo(() => {
    const total = displayStats.entered || 1;
    return {
      completed: Math.round((displayStats.completed / total) * 100),
      running: Math.round((displayStats.running / total) * 100),
      other: Math.round(((total - displayStats.completed - displayStats.running) / total) * 100),
    };
  }, [displayStats]);

  // ========== ROW-LEVEL LOADING STATE ==========
  // Tracks which runs are currently processing an action
  // Shape: { [runId]: 'pausing' | 'resuming' | 'stopping' | 'deleting' }
  const [loadingRuns, setLoadingRuns] = useState({});

  // Error toast state for failed actions
  const [actionError, setActionError] = useState(null);
  const errorTimerRef = useRef(null);

  const showActionError = (message) => {
    setActionError(message);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setActionError(null), 3500);
  };

  // Cleanup error timer on unmount
  useEffect(() => {
    return () => { if (errorTimerRef.current) clearTimeout(errorTimerRef.current); };
  }, []);

  const setRunLoading = (runId, action) => {
    setLoadingRuns(prev => ({ ...prev, [runId]: action }));
  };
  const clearRunLoading = (runId) => {
    setLoadingRuns(prev => {
      const next = { ...prev };
      delete next[runId];
      return next;
    });
  };

  // Action handlers (async with loading + error feedback)
  const handlePause = async (run) => {
    if (run._isMock || loadingRuns[run.id]) return;
    setRunLoading(run.id, 'pausing');
    try {
      await onPause?.(run.id);
    } catch (error) {
      console.error('Failed to pause run:', error);
      showActionError('Failed to pause lead. Please try again.');
    } finally {
      clearRunLoading(run.id);
    }
  };

  const handleResume = async (run) => {
    if (run._isMock || loadingRuns[run.id]) return;
    setRunLoading(run.id, 'resuming');
    try {
      await onResume?.(run.id);
    } catch (error) {
      console.error('Failed to resume run:', error);
      showActionError('Failed to resume lead. Please try again.');
    } finally {
      clearRunLoading(run.id);
    }
  };

  const handleStop = async (run) => {
    if (run._isMock || loadingRuns[run.id]) return;
    setRunLoading(run.id, 'stopping');
    try {
      await onStop?.(run.id);
    } catch (error) {
      console.error('Failed to stop run:', error);
      showActionError('Failed to stop lead. Please try again.');
    } finally {
      clearRunLoading(run.id);
    }
  };

  const handleDelete = async (run) => {
    if (run._isMock || loadingRuns[run.id]) return;
    setRunLoading(run.id, 'deleting');
    try {
      await onDelete?.(run.id);
    } catch (error) {
      console.error('Failed to delete run:', error);
      showActionError('Failed to delete lead. Please try again.');
    } finally {
      clearRunLoading(run.id);
    }
  };

  // Inline action menu component for each run row
  const RunActionMenu = ({ run }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    const isLoading = !!loadingRuns[run.id];
    const loadingAction = loadingRuns[run.id];

    useEffect(() => {
      if (!open) return;
      const handleClickOutside = (e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const status = (run.status || '').toLowerCase();
    const canPause = status === 'running' || status === 'waiting';
    const canResume = status === 'paused';
    const canStop = status === 'running' || status === 'waiting' || status === 'paused';
    const canDelete = true; // Any status can be deleted

    // Show spinner when action is in progress
    if (isLoading) {
      const actionLabels = {
        pausing: 'Pausing…',
        resuming: 'Resuming…',
        stopping: 'Stopping…',
        deleting: 'Deleting…',
      };
      return (
        <div className="flex items-center gap-1.5 text-stone-400 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>{actionLabels[loadingAction] || 'Processing…'}</span>
        </div>
      );
    }

    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
          className="p-1 hover:bg-stone-100 rounded text-stone-400 hover:text-stone-600 transition"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50 min-w-[140px]">
            {canPause && (
              <button
                onClick={() => { handlePause(run); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 transition"
              >
                <Pause className="w-3 h-3" /> Pause
              </button>
            )}
            {canResume && (
              <button
                onClick={() => { handleResume(run); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50 transition"
              >
                <Play className="w-3 h-3" /> Resume
              </button>
            )}
            {canStop && (
              <button
                onClick={() => { handleStop(run); setOpen(false); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-orange-600 hover:bg-orange-50 transition"
              >
                <StopCircle className="w-3 h-3" /> Stop
              </button>
            )}
            {canDelete && (
              <>
                {(canPause || canResume || canStop) && <div className="border-t border-stone-100 my-1" />}
                <button
                  onClick={() => { handleDelete(run); setOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };
  if (isMobile) {
    if (!showLeadsPanel) return null;

    return (
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-notion-border shadow-floating z-30 flex flex-col h-[85%] rounded-t-2xl animate-slideUp">
        <div className="px-5 py-4 border-b border-notion-border flex items-center justify-between bg-white flex-shrink-0 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
              <Activity className="w-4 h-4 text-stone-500" /> Lead Monitor
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 bg-stone-100 rounded-full text-stone-500"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 bg-stone-50 border-b border-notion-border flex flex-col gap-3">
          <div className="flex flex-col gap-1 w-full">
            <div className="flex justify-between text-[10px] text-stone-500 font-medium">
              <span>{displayStats.entered} Entered</span>
              <span>{displayStats.completed} Completed</span>
            </div>
            <div className="h-2 w-full bg-stone-100 rounded-full overflow-hidden flex">
              <div className="h-full bg-green-400" style={{ width: `${progressPcts.completed}%` }}></div>
              <div className="h-full bg-orange-400" style={{ width: `${progressPcts.running}%` }}></div>
              <div className="h-full bg-stone-300" style={{ width: `${progressPcts.other}%` }}></div>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <select
              className="text-xs bg-white border border-notion-border rounded px-2 py-2 outline-none text-stone-600 flex-1 min-w-[120px]"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
            >
              <option value="all">Status: All</option>
              <option value="Running">Running</option>
              <option value="Waiting">Waiting</option>
              <option value="Paused">Paused</option>
              <option value="Completed">Completed</option>
              <option value="Stopped">Stopped</option>
            </select>
            <select
              className="text-xs bg-white border border-notion-border rounded px-2 py-2 outline-none text-stone-600 flex-1 min-w-[120px]"
              value={sourceFilter}
              onChange={(e) => onSourceFilterChange && onSourceFilterChange(e.target.value)}
            >
              <option value="all">Source: All</option>
              <option value="Form">Form</option>
              <option value="Manual">Manual</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-stone-50/30">
          {normalizedRuns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-stone-400 p-6">
              <Activity className="w-8 h-8 mb-2" />
              <p className="text-sm font-medium">No leads in this workflow yet</p>
              <p className="text-xs">Leads will appear here when they enter the automation</p>
            </div>
          ) : (
            <div className="divide-y divide-notion-border">
              {normalizedRuns.map((run) => (
                <div key={run.id} className={`p-4 flex flex-col gap-1 ${run.status !== 'running' ? 'bg-stone-50/50' : 'bg-white'}`}>
                  <div className="flex justify-between">
                    <span className={`font-medium text-sm ${run.status !== 'running' ? 'text-stone-500' : 'text-stone-800'}`}>{run.name}</span>
                    <span className={`px-2 py-0.5 rounded border font-medium text-[10px] flex items-center gap-1 ${getStatusStyle(run.status)}`}>
                      <StatusIcon status={run.status} />
                      {run.displayStatus}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs text-stone-500">Interest: {run.property}</span>
                    {run.extra && <span className="text-[10px] text-stone-400">{run.extra}</span>}
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    {run.pauseReason === 'lead_replied' ? (
                      <span className="text-xs text-orange-600 flex items-center gap-1 font-medium">
                        <MessageCircle className="w-3 h-3" /> Lead Replied
                      </span>
                    ) : run.pauseReason === 'no_interested_property' ? (
                      <span className="text-xs text-red-600 flex items-center gap-1 font-medium">
                        <XCircle className="w-3 h-3" /> No Interested Property
                      </span>
                    ) : run.isCompleted ? (
                      <span className="text-xs text-green-600 font-medium">Completed</span>
                    ) : (
                      <span className="text-xs text-stone-400">Step {(run.currentStepIndex || 0) + 1}/{run.totalSteps || '?'}: {run.step}</span>
                    )}
                    <RunActionMenu run={run} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error toast for failed actions */}
        {actionError && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50">
            <div className="bg-red-600 text-white text-xs font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
              <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {actionError}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`absolute bottom-0 left-0 right-0 bg-white border-t border-notion-border shadow-floating z-30 flex flex-col h-[400px] transform transition-transform duration-300 ${showLeadsPanel ? 'translate-y-0' : 'translate-y-full'}`}>
      <div className="px-6 py-3 border-b border-notion-border flex items-center justify-between bg-white flex-shrink-0">
        <div className="flex items-center gap-6">
          <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
            <Activity className="w-4 h-4 text-stone-500" /> Lead Monitor
            {isLoading && <Loader2 className="w-3 h-3 animate-spin text-stone-400" />}
          </h3>

          <div className="flex flex-col gap-1 w-64">
            <div className="flex justify-between text-[10px] text-stone-500 font-medium">
              <span>{displayStats.entered} Entered</span>
              <span>{displayStats.completed} Completed</span>
            </div>
            <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden flex">
              <div className="h-full bg-green-400" style={{ width: `${progressPcts.completed}%` }}></div>
              <div className="h-full bg-orange-400" style={{ width: `${progressPcts.running}%` }}></div>
              <div className="h-full bg-stone-300" style={{ width: `${progressPcts.other}%` }}></div>
            </div>
          </div>

          <div className="h-4 w-px bg-stone-200"></div>

          <div className="flex gap-2">
            <select
              className="text-xs bg-stone-50 border border-notion-border rounded px-2 py-1.5 outline-none text-stone-600 focus:border-stone-400"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
            >
              <option value="all">Status: All</option>
              <option value="Running">Running</option>
              <option value="Waiting">Waiting</option>
              <option value="Paused">Paused</option>
              <option value="Completed">Completed</option>
              <option value="Stopped">Stopped</option>
            </select>
            <select
              className="text-xs bg-stone-50 border border-notion-border rounded px-2 py-1.5 outline-none text-stone-600 focus:border-stone-400"
              value={sourceFilter}
              onChange={(e) => onSourceFilterChange && onSourceFilterChange(e.target.value)}
            >
              <option value="all">Source: All</option>
              <option value="Form">Form</option>
              <option value="Manual">Manual</option>
            </select>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded text-stone-400 hover:text-stone-600 transition">
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-stone-50/30">
        {normalizedRuns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-stone-400">
            <Activity className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">No leads in this workflow yet</p>
            <p className="text-xs mt-1">Leads will appear here when they enter the automation</p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="bg-stone-50 sticky top-0 z-10 text-[10px] font-bold text-stone-400 uppercase tracking-wider border-b border-notion-border">
              <tr>
                <th className="px-6 py-3 font-medium">Lead Name</th>
                <th className="px-6 py-3 font-medium">Property Interest(s)</th>
                <th className="px-6 py-3 font-medium">Current Step</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Reason/Note</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-notion-border text-xs text-stone-700 bg-white">
              {normalizedRuns.map((run) => (
                <tr key={run.id} className={`hover:bg-notion-hover group transition-colors ${run.status !== 'running' ? 'bg-stone-50/50' : ''}`}>
                  <td className={`px-6 py-3 font-medium ${run.status !== 'running' ? 'text-stone-500' : ''}`}>{run.name}</td>
                  <td className={`px-6 py-3 ${run.status !== 'running' ? 'text-stone-400' : 'text-stone-500'}`}>
                    <div className="flex flex-col">
                      <span>{run.property}</span>
                      {run.extra && <span className="text-[10px] text-stone-400">{run.extra}</span>}
                    </div>
                  </td>
                  <td className={`px-6 py-3 ${run.status !== 'running' ? 'text-stone-400' : ''}`}>
                    {run.isCompleted ? (
                      <span className="text-green-600 font-medium">Completed</span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className="text-stone-300">{(run.currentStepIndex || 0) + 1}/{run.totalSteps || '?'}</span>
                        {run.step}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded border font-medium flex items-center gap-1 w-fit ${getStatusStyle(run.status)}`}>
                      <StatusIcon status={run.status} />
                      {run.displayStatus}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    {run.pauseReason === 'lead_replied' ? (
                      <div className="flex items-center gap-1.5 text-stone-600">
                        <MessageCircle className="w-3 h-3 text-orange-500" /> Lead Replied
                      </div>
                    ) : run.pauseReason === 'no_interested_property' ? (
                      <div className="flex items-center gap-1.5 text-red-600">
                        <XCircle className="w-3 h-3 text-red-500" /> No Interested Property
                      </div>
                    ) : (
                      <span className={run.status !== 'running' ? 'text-stone-500 text-[10px]' : 'text-stone-400'}>{run.note}</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <RunActionMenu run={run} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Error toast for failed actions */}
      {actionError && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-red-600 text-white text-xs font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
            <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {actionError}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsMonitorPanel;
